import {type MatchParams} from '@wandb/weave/common/types/base';
import {decodeURIComponentSafe} from '@wandb/weave/common/util/url';

import {
  AGENT_LENS_PRODUCT_ID,
  NOTEBOOKS_PRODUCT_ID,
  POST_TRAINING_PRODUCT_ID,
  REGISTRY_PRODUCT_ID,
  SANDBOXES_PRODUCT_ID,
  WANDB_PRODUCT_ID,
} from '../../../shellRemote';

/** The W&B entity and project ARIA scopes its chat to on the current page. */
export type AriaPageScope = {
  entityName?: string;
  projectName?: string;
  /**
   * The W&B route matcher's params on W&B-rendered pages, and an empty object
   * everywhere else. Legacy: only analytics reads it, to derive its `location`.
   */
  legacyWBMatchParams: MatchParams;
};

// Model Distillation: /distillation[/weave|/postgres]/e/{entity}/...
const DISTILLATION_ROUTE =
  /^\/distillation(?:\/(?:weave|postgres))?\/e\/([^/]+)(?:\/|$)/;
// Agent Lens: /{entity}/{project}/...
const AGENT_LENS_ROUTE = /^\/([^/]+)\/([^/]+)(?:\/|$)/;

// Shared so off-W&B scopes keep a stable `legacyWBMatchParams` across renders.
const NO_WB_MATCH_PARAMS: MatchParams = {};
const NO_SCOPE: AriaPageScope = {legacyWBMatchParams: NO_WB_MATCH_PARAMS};

/**
 * Page scope by the Forge product serving the page.
 *
 * - Outside the Forge shell, and on the W&B-rendered products whose routes
 *   carry an entity or project (W&B, Registry, Notebooks and Sandboxes), the
 *   W&B route matcher's params apply. Inference and the Post-Training landing
 *   page are W&B-rendered too, but their routes carry neither, and the matcher
 *   reads deeper `/post-training/...` paths as entity `post-training`, so they
 *   are left out.
 * - Products with a known URL shape are parsed here. Distillation gives the
 *   entity only: its URL holds a proxy-model alias, which isn't always the
 *   W&B project name.
 * - Any other product gets no scope, since W&B's matcher would misread its URL.
 */
export function resolveAriaPageScope(
  activeProductId: string | undefined,
  pathname: string,
  routeParams: MatchParams,
  isInShell: boolean
): AriaPageScope {
  if (!isInShell) {
    return scopeFromRouteParams(routeParams);
  }
  switch (activeProductId) {
    case WANDB_PRODUCT_ID:
    case REGISTRY_PRODUCT_ID:
    case NOTEBOOKS_PRODUCT_ID:
    case SANDBOXES_PRODUCT_ID:
      return scopeFromRouteParams(routeParams);
    case POST_TRAINING_PRODUCT_ID: {
      const match = DISTILLATION_ROUTE.exec(pathname);
      return match
        ? {
            entityName: decodeURIComponentSafe(match[1]),
            legacyWBMatchParams: NO_WB_MATCH_PARAMS,
          }
        : NO_SCOPE;
    }
    case AGENT_LENS_PRODUCT_ID: {
      const match = AGENT_LENS_ROUTE.exec(pathname);
      return match
        ? {
            entityName: decodeURIComponentSafe(match[1]),
            projectName: decodeURIComponentSafe(match[2]),
            legacyWBMatchParams: NO_WB_MATCH_PARAMS,
          }
        : NO_SCOPE;
    }
    default:
      return NO_SCOPE;
  }
}

function scopeFromRouteParams(params: MatchParams): AriaPageScope {
  return {
    entityName: params.entityName,
    projectName: params.projectName,
    legacyWBMatchParams: params,
  };
}
